import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useOutletContext, useParams, useSearchParams } from 'react-router-dom';

import moment from 'moment';

import { MasterNameApi } from '@/api';
import { DriverLicenseApi } from '@/api/api.driver.license';
import { DisabledOverlay } from '@/components/DisabledOverlay';
import { VerifiedFieldsProvider } from '@/contexts';
import { useKaseContext } from '@/hooks';
import { useTabLayoutContext } from '@/layouts/TabLayout';
import { IMasterNamePerson, MasterNameInvolvement } from '@/models';
import { MasterIndexDetailsContext } from '@/pages/MasterIndex/components/MasterIndexDetailsPage';
import { useToastStore } from '@/store';
import { ConfirmTypes } from '@/store/store.toast';
import { generateUniqueId, parsePhoneNumber } from '@/utils';

import { useMasterNamesContext } from '..';
import { MasterAddDriverLicense } from './MasterAddDriverLicense';
import { MasterLookupName } from './MasterLookupName';
import { MasterNameDriverLicense } from './MasterNameDriverLicense';
import { MasterNameDriverLicenseView } from './MasterNameDriverLicenseView';
import MasterNameForm from './MasterNameForm';
import { TabNamesScreens } from '../data';

type MasterAddNameDetailsProps = {
  isMasterIndex?: boolean;
  hideHeader?: boolean;
  defaultIsVerified?: boolean;
  onGoBack?: () => void;
  onCustomSubmit?: (values: any) => Promise<void>;
  onAfterSubmit?: (personId: string) => Promise<void>;
};

export const MasterAddNameDetails = ({
  isMasterIndex,
  hideHeader,
  defaultIsVerified = false,
  onGoBack,
  onCustomSubmit,
  onAfterSubmit,
}: MasterAddNameDetailsProps) => {
  const { updateToast } = useToastStore();
  const {
    currentMastername,
    setCurrentMastername,
    temporaryDriverLicense,
    setTemporaryDriverLicense,
    handleUpdateMasterName,
    handleAddLinkedName,
  } = useMasterNamesContext();
  const { currentScreen } = useTabLayoutContext();
  const { kase } = useKaseContext();
  const { id: personId } = useParams();
  const [searchParams] = useSearchParams();

  const outletContext = useOutletContext<
    MasterIndexDetailsContext | undefined
  >();
  const setCustomTitle = outletContext?.setCustomTitle;
  const setLastUpdatedAt = outletContext?.setLastUpdatedAt;

  const [initialVerifiedFields, setInitialVerifiedFields] = useState<
    Record<string, any> | undefined
  >(undefined);

  const methods = useForm<
    IMasterNamePerson & {
      ssn_prev: string;
      involvement: MasterNameInvolvement[];
    }
  >({
    defaultValues: {
      involvement: Array.isArray(currentMastername?.relationship?.involvement)
        ? (currentMastername?.relationship
            ?.involvement as MasterNameInvolvement[])
        : [],
      firstName: '',
      lastName: '',
      isVerified: defaultIsVerified,
      physicalCharacteristics: {
        others: [],
      },
    },
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (currentMastername?.masterName._id) fetchMasterNameData();
  }, [currentMastername?.masterName._id]);

  const fetchMasterNameData = async () => {
    try {
      const currentPersonId = personId || searchParams.get('id');
      const res = currentPersonId
        ? await MasterNameApi.getMasterName(currentPersonId)
        : await MasterNameApi.getMasterName(
            String(currentMastername?.masterName._id),
          );

      const formData = {
        ...res.data,
        employerInfo: {
          ...res.data.employerInfo,
          phoneNumber: parsePhoneNumber(res.data.employerInfo?.phoneNumber),
        },
        phoneNumbers: res.data.phoneNumbers?.map((phone) => ({
          ...phone,
          phoneNumber: parsePhoneNumber(phone.phoneNumber),
        })),
        ssn_prev: res.data.ssn,
        involvement: Array.isArray(currentMastername?.relationship?.involvement)
          ? (currentMastername?.relationship
              ?.involvement as MasterNameInvolvement[])
          : currentMastername?.relationship?.involvement
          ? [
              currentMastername?.relationship
                ?.involvement as MasterNameInvolvement,
            ]
          : [],
        emails:
          Array.isArray(res.data?.emails) && res.data.emails.length > 0
            ? (res.data.emails.map((email: any) => ({
                email:
                  typeof email === 'string'
                    ? email
                    : typeof email === 'object' && email?.email
                    ? email.email
                    : '',
              })) as any)
            : [],
      };

      methods.reset(formData);

      if (res.data.idScanVerifiedFields) {
        setInitialVerifiedFields(res.data.idScanVerifiedFields);
      }

      // Set the currentMastername immediately with the fetched data
      if (currentPersonId) {
        // if data was fetched from paramId and not already available currentMasterName
        setCurrentMastername({
          masterName: res.data,
          relationship: {
            involvement: undefined,
            behavior: 'N/A',
          },
          kaseId: '',
          createdAt: new Date(),
          updatedAt: new Date(),
        } as any);

        if (res.data.uniqueId && isMasterIndex && setCustomTitle) {
          setCustomTitle(
            `${generateUniqueId('PER', res.data.uniqueId)} - ${
              res.data.firstName
            } ${res.data.lastName}`,
          );
        }
      }
      setLastUpdatedAt?.(res.data?.updatedAt);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  useEffect(() => {
    if (personId || searchParams.get('id')) {
      fetchMasterNameData();
    }
  }, [personId, searchParams.get('id')]);

  // Update the handleSaveTemporaryDriverLicense function to use the correct firstName/lastName values
  const handleSaveTemporaryDriverLicense = async (masterNameId: string) => {
    if (!temporaryDriverLicense) return;
    if (!masterNameId) {
      updateToast({
        open: true,
        message: 'Error: Missing person ID for license update',
        type: ConfirmTypes.ERROR,
      });
      return;
    }

    try {
      // Format dates properly for driver license
      const formattedDriverLicense = {
        ...temporaryDriverLicense,
        // Convert date objects or strings to formatted strings
        birthday: moment(temporaryDriverLicense.birthday).format('YYYY-MM-DD'),
        expireDate: moment(temporaryDriverLicense.expireDate).format(
          'YYYY-MM-DD',
        ),
        issueDate: moment(temporaryDriverLicense.issueDate).format(
          'YYYY-MM-DD',
        ),
        // Ensure address is properly handled for API
        address:
          typeof temporaryDriverLicense.address === 'object' &&
          temporaryDriverLicense.address?._id
            ? temporaryDriverLicense.address._id
            : temporaryDriverLicense.address || '',
      };

      // Save driver license with proper formatting
      const savedLicense = await DriverLicenseApi.saveDriverLicense(
        formattedDriverLicense as any,
      );

      if (!savedLicense?.data?._id) {
        updateToast({
          open: true,
          message: 'Failed to save driver license: No ID returned',
          type: ConfirmTypes.ERROR,
        });
        return;
      }

      // First, get the latest person data to ensure we have the correct firstName and lastName
      try {
        const personData = await MasterNameApi.getMasterName(masterNameId);

        if (!personData?.data) {
          updateToast({
            open: true,
            message: 'Could not fetch person data for update',
            type: ConfirmTypes.ERROR,
          });
          return;
        }

        // Prepare update data - use the person's actual values, not the license values
        const updateData = {
          firstName: personData.data.firstName || 'Unknown',
          lastName: personData.data.lastName || 'Unknown',
          driverLicense: savedLicense.data._id,
        };

        // Update the person with the license ID
        await handleUpdateMasterName(masterNameId, updateData);

        // Double-check the association
        const verifyPerson = await MasterNameApi.getMasterName(masterNameId);

        if (!verifyPerson.data.driverLicense) {
          // Driver license not properly linked in verification check
        }

        // Clear temporary data
        setTemporaryDriverLicense(null);

        updateToast({
          open: true,
          message: 'License saved successfully',
          type: ConfirmTypes.SUCCESS,
        });
      } catch (fetchErr: any) {
        updateToast({
          open: true,
          message: `Failed to update person with license: ${fetchErr.message}`,
          type: ConfirmTypes.ERROR,
        });
      }
    } catch (err: any) {
      updateToast({
        open: true,
        message: `Error saving driver license: ${err.message}`,
        type: ConfirmTypes.ERROR,
      });
    }
  };

  // Update the onSubmitWithDriverLicense function to filter out invalid addresses
  const onSubmitWithDriverLicense = async (values: any) => {
    try {
      // If current master name is temporary and we have temporary driver license data
      if ((currentMastername as any)?.isTemporary && temporaryDriverLicense) {
        // Format phone numbers correctly before submission
        if (values.phoneNumbers?.length) {
          values.phoneNumbers = values.phoneNumbers
            .filter(({ type, phoneNumber }: any) => type && phoneNumber !== '')
            .map((phone: any) => ({
              ...phone,
              phoneNumber: parsePhoneNumber(phone.phoneNumber, '+1'),
            }));
        }

        if (values.employerInfo?.phoneNumber) {
          values.employerInfo.phoneNumber = parsePhoneNumber(
            values.employerInfo.phoneNumber,
            '+1',
          );
        }

        // Filter out incomplete addresses
        if (values.addresses?.length) {
          values.addresses = values.addresses.filter((addr: any) => {
            // Check if address has all required fields
            return (
              addr &&
              addr.pointType &&
              addr.zipName &&
              addr.zipcode &&
              addr.state &&
              addr.addressNumber &&
              addr.completeStreetName
            );
          });
        }

        try {
          // Create master name person first - ensure correct API
          const res = await MasterNameApi.createMasterNamePerson(values);

          // If successful, save driver license and link it
          if (res?.data?._id) {
            await handleSaveTemporaryDriverLicense(res.data._id);

            // Update current master name to non-temporary
            setCurrentMastername({
              kaseId: '',
              relationship: {
                behavior: 'N/A',
                involvement: undefined,
              },
              masterName: res.data as IMasterNamePerson,
              createdAt: new Date(),
              updatedAt: new Date(),
            } as any);

            handleAddLinkedName(res?.data?._id, {
              relationship: {
                behavior: 'N/A',
                involvement: kase?._id ? undefined : [],
              },
            });
            updateToast({
              open: true,
              message: 'Person and driver license saved successfully',
              type: ConfirmTypes.SUCCESS,
            });

            // Return to list or previous screen
            if (onGoBack) onGoBack();
            return;
          }
        } catch (error: any) {
          updateToast({
            open: true,
            message: `Error saving person with license: ${error.message}`,
            type: ConfirmTypes.ERROR,
          });
        }
      }

      // Default fallback - just submit the form normally
      if (onGoBack) onGoBack();
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case TabNamesScreens.MasterLookupName:
        return <MasterLookupName />;
      case TabNamesScreens.MasterNameDriverLicense:
        return currentMastername ? <MasterNameDriverLicense /> : null;
      case TabNamesScreens.MasterAddDriverLicense:
        return currentMastername ? <MasterAddDriverLicense /> : null;
      case TabNamesScreens.MasterNameDriverLicenseView:
        return currentMastername ? <MasterNameDriverLicenseView /> : null;
      default:
        return (
          <FormProvider {...methods}>
            <VerifiedFieldsProvider
              initialVerifiedFields={initialVerifiedFields}
            >
              <MasterNameForm
                hideHeader={hideHeader}
                onGoBack={onGoBack}
                isMasterIndex={isMasterIndex}
                onAfterSubmit={onAfterSubmit}
                onCustomSubmit={
                  (currentMastername as any)?.isTemporary &&
                  temporaryDriverLicense
                    ? onSubmitWithDriverLicense
                    : onCustomSubmit
                }
              />
              {kase && <DisabledOverlay />}
            </VerifiedFieldsProvider>
          </FormProvider>
        );
    }
  };

  return renderCurrentScreen();
};
